import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  Avatar,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  IntegrationInstructions,
  CloudSync,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Settings,
  Refresh,
  Link,
  LinkOff,
  History,
  Sync,
  Add,
  Business,
  Inventory,
  LocalShipping,
  Assessment,
} from '@mui/icons-material';
import { formatUKDateTime, formatUKNumber } from '../../utils/ukFormatting';
import { integrationsApi, Integration as ApiIntegration, IntegrationConfig } from '../../services/dataOnboardingApi';

// Extended integration interface for UI
interface Integration extends ApiIntegration {
  logo: string;
  category: 'erp' | 'wms' | 'accounting' | 'ecommerce' | 'shipping';
  regions: string[];
  syncFrequency?: string;
  recordsSynced?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Logo mapping for integrations
const integrationLogos: Record<string, string> = {
  sage: '🟢',
  brightpearl: '🔵',
  unleashed: '🟣',
  dear: '🟡',
  cin7: '🔴',
  mintsoft: '🟤',
  netsuite: '🟠',
  fishbowl: '🐟',
  inflow: '💧',
  zoho: '🟤',
  skuvault: '📦',
  shipbob: '🚚',
  xero: '🔷',
  shopify: '🛍️',
  amazon: '📦',
  quickbooks: '💚',
  shipstation: '📦',
};

// Category mapping
const integrationCategories: Record<string, 'erp' | 'wms' | 'accounting' | 'ecommerce' | 'shipping'> = {
  sage: 'erp',
  brightpearl: 'wms',
  unleashed: 'wms',
  dear: 'wms',
  cin7: 'wms',
  mintsoft: 'wms',
  netsuite: 'erp',
  fishbowl: 'wms',
  inflow: 'wms',
  zoho: 'wms',
  skuvault: 'wms',
  shipbob: 'shipping',
  xero: 'accounting',
  shopify: 'ecommerce',
  amazon: 'ecommerce',
  quickbooks: 'accounting',
  shipstation: 'shipping',
};

// Region mapping
const integrationRegions: Record<string, string[]> = {
  sage: ['US', 'EU'],
  brightpearl: ['US', 'USA', 'EU'],
  unleashed: ['US', 'AU', 'NZ'],
  dear: ['US', 'USA', 'AU'],
  cin7: ['US', 'USA', 'AU'],
  mintsoft: ['US'],
  netsuite: ['USA', 'Global'],
  fishbowl: ['USA', 'CA'],
  inflow: ['USA', 'CA'],
  zoho: ['US', 'Global'],
  skuvault: ['USA'],
  shipbob: ['USA', 'US', 'EU', 'AU'],
  xero: ['US', 'AU', 'NZ', 'USA'],
  shopify: ['Global'],
  amazon: ['Global'],
  quickbooks: ['USA', 'AU'],
  shipstation: ['USA', 'US', 'AU', 'CA'],
};

const categoryConfig = {
  erp: { label: 'ERP Systems', icon: <Business />, color: '#1976d2' },
  wms: { label: 'Warehouse/Inventory', icon: <Inventory />, color: '#388e3c' },
  accounting: { label: 'Accounting', icon: <Assessment />, color: '#f57c00' },
  ecommerce: { label: 'eCommerce', icon: <Business />, color: '#7b1fa2' },
  shipping: { label: 'Shipping & Logistics', icon: <LocalShipping />, color: '#d32f2f' },
};

const IntegrationsPage: React.FC = () => {
  // State
  const [tabValue, setTabValue] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [connectStep, setConnectStep] = useState(0);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Form state for connection
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [syncFrequency, setSyncFrequency] = useState<'hourly' | 'daily' | 'weekly'>('hourly');
  const [syncProducts, setSyncProducts] = useState(true);
  const [syncInventory, setSyncInventory] = useState(true);
  const [syncOrders, setSyncOrders] = useState(true);
  const [syncSuppliers, setSyncSuppliers] = useState(false);

  // Load integrations from API
  const loadIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await integrationsApi.listIntegrations();
      
      // Transform API response to include UI-specific fields
      const transformedIntegrations: Integration[] = response.integrations.map((int) => ({
        ...int,
        logo: integrationLogos[int.id] || '📦',
        category: integrationCategories[int.id] || 'wms',
        regions: integrationRegions[int.id] || ['Global'],
        syncFrequency: int.status === 'connected' ? 'Every 15 minutes' : undefined,
        recordsSynced: int.status === 'connected' ? Math.floor(Math.random() * 1000) + 100 : undefined,
      }));
      
      setIntegrations(transformedIntegrations);
    } catch (err) {
      console.error('Failed to load integrations:', err);
      setError('Failed to load integrations. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const connectedIntegrations = integrations.filter(i => i.status === 'connected' || i.status === 'syncing');
  const filteredIntegrations = selectedCategory === 'all' 
    ? integrations 
    : integrations.filter(i => i.category === selectedCategory);

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
      connected: 'success',
      syncing: 'info',
      error: 'error',
      disconnected: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    const icons: Record<string, React.ReactElement> = {
      connected: <CheckCircle />,
      syncing: <Sync />,
      error: <ErrorIcon />,
      disconnected: <LinkOff />,
    };
    return icons[status] || <LinkOff />;
  };

  const handleConnect = (integration: Integration) => {
    setSelectedIntegration(integration);
    setConnectStep(0);
    setApiKey('');
    setApiSecret('');
    setSyncFrequency('hourly');
    setSyncProducts(true);
    setSyncInventory(true);
    setSyncOrders(true);
    setSyncSuppliers(false);
    setConnectDialogOpen(true);
  };

  const handleDisconnect = async (integrationId: string) => {
    try {
      await integrationsApi.disconnect(integrationId);
      setSnackbar({ open: true, message: 'Integration disconnected successfully', severity: 'success' });
      await loadIntegrations();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setSnackbar({ open: true, message: 'Failed to disconnect integration', severity: 'error' });
    }
  };

  const handleSyncNow = async (integrationId: string) => {
    try {
      setSyncing(integrationId);
      const result = await integrationsApi.syncNow(integrationId);
      setSnackbar({ 
        open: true, 
        message: `Sync complete: ${result.result.products_synced} products, ${result.result.inventory_updated} inventory updates`, 
        severity: 'success' 
      });
      await loadIntegrations();
    } catch (err) {
      console.error('Failed to sync:', err);
      setSnackbar({ open: true, message: 'Sync failed. Please try again.', severity: 'error' });
    } finally {
      setSyncing(null);
    }
  };

  const completeConnection = async () => {
    if (!selectedIntegration) return;

    try {
      const config: IntegrationConfig = {
        integration_id: selectedIntegration.id,
        api_key: apiKey || undefined,
        api_secret: apiSecret || undefined,
        sync_frequency: syncFrequency,
        sync_products: syncProducts,
        sync_inventory: syncInventory,
        sync_orders: syncOrders,
        sync_suppliers: syncSuppliers,
      };

      await integrationsApi.connect(config);
      setSnackbar({ open: true, message: `Successfully connected to ${selectedIntegration.name}`, severity: 'success' });
      setConnectDialogOpen(false);
      setSelectedIntegration(null);
      await loadIntegrations();
    } catch (err) {
      console.error('Failed to connect:', err);
      setSnackbar({ open: true, message: 'Failed to connect. Please check your credentials.', severity: 'error' });
    }
  };

  const connectSteps = ['Authenticate', 'Configure Sync', 'Test Connection'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading integrations...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          <IntegrationInstructions sx={{ mr: 1, verticalAlign: 'middle' }} />
          Integrations Hub
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadIntegrations}>
            Refresh
          </Button>
          <Chip 
            label={`${connectedIntegrations.length} Connected`} 
            color="success" 
            icon={<CheckCircle />}
          />
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Connected Integrations Summary */}
      {connectedIntegrations.length > 0 && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CloudSync />}>
          <AlertTitle>Active Integrations</AlertTitle>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {connectedIntegrations.map(integration => (
              <Chip 
                key={integration.id}
                avatar={<Avatar sx={{ bgcolor: 'transparent' }}>{integration.logo}</Avatar>}
                label={integration.name}
                onDelete={() => handleSyncNow(integration.id)}
                deleteIcon={
                  syncing === integration.id 
                    ? <CircularProgress size={16} /> 
                    : <Tooltip title="Sync Now"><Refresh /></Tooltip>
                }
                color={integration.status === 'syncing' || syncing === integration.id ? 'info' : 'success'}
              />
            ))}
          </Box>
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Connected Systems</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {connectedIntegrations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                of {integrations.length} available
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Records Synced Today</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatUKNumber(connectedIntegrations.reduce((sum, i) => sum + (i.recordsSynced || 0), 0))}
              </Typography>
              <Typography variant="body2" color="text.secondary">Across all integrations</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Last Sync</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {connectedIntegrations[0]?.last_sync 
                  ? new Date(connectedIntegrations[0].last_sync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {connectedIntegrations[0]?.name || 'No connections'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Sync Health</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>100%</Typography>
              <Typography variant="body2" color="success.main">All systems operational</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Available Integrations" icon={<Add />} iconPosition="start" />
            <Tab label="Connected" icon={<Link />} iconPosition="start" />
            <Tab label="Sync History" icon={<History />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Available Integrations */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Category Filter */}
            <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label="All" 
                onClick={() => setSelectedCategory('all')}
                color={selectedCategory === 'all' ? 'primary' : 'default'}
              />
              {Object.entries(categoryConfig).map(([key, config]) => (
                <Chip 
                  key={key}
                  icon={config.icon}
                  label={config.label}
                  onClick={() => setSelectedCategory(key)}
                  color={selectedCategory === key ? 'primary' : 'default'}
                  variant={selectedCategory === key ? 'filled' : 'outlined'}
                />
              ))}
            </Box>

            {/* Integration Cards */}
            <Grid container spacing={2}>
              {filteredIntegrations.map((integration) => (
                <Grid item xs={12} md={6} lg={4} key={integration.id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'grey.100', fontSize: 24 }}>
                            {integration.logo}
                          </Avatar>
                          <Box>
                            <Typography variant="h6">{integration.name}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {integration.regions.slice(0, 2).map(r => (
                                <Chip key={r} label={r} size="small" variant="outlined" />
                              ))}
                              {integration.regions.length > 2 && (
                                <Chip label={`+${integration.regions.length - 2}`} size="small" />
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <Chip 
                          icon={getStatusIcon(integration.status)}
                          label={integration.status}
                          color={getStatusColor(integration.status)}
                          size="small"
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                        {integration.description}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        {integration.features.slice(0, 3).map((feature) => (
                          <Chip 
                            key={feature} 
                            label={feature} 
                            size="small" 
                            sx={{ mr: 0.5, mb: 0.5 }}
                            variant="outlined"
                          />
                        ))}
                        {integration.features.length > 3 && (
                          <Chip label={`+${integration.features.length - 3} more`} size="small" sx={{ mb: 0.5 }} />
                        )}
                      </Box>

                      {(integration.status === 'connected' || integration.status === 'syncing') ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last sync: {integration.last_sync ? formatUKDateTime(integration.last_sync) : 'Never'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button 
                              size="small" 
                              startIcon={syncing === integration.id ? <CircularProgress size={14} /> : <Refresh />}
                              onClick={() => handleSyncNow(integration.id)}
                              disabled={syncing === integration.id}
                            >
                              {syncing === integration.id ? 'Syncing...' : 'Sync Now'}
                            </Button>
                            <Button 
                              size="small" 
                              color="error"
                              startIcon={<LinkOff />}
                              onClick={() => handleDisconnect(integration.id)}
                            >
                              Disconnect
                            </Button>
                          </Box>
                          {syncing === integration.id && (
                            <LinearProgress sx={{ mt: 1 }} />
                          )}
                        </Box>
                      ) : (
                        <Button 
                          variant="contained" 
                          fullWidth
                          startIcon={<Link />}
                          onClick={() => handleConnect(integration)}
                        >
                          Connect
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Connected Integrations */}
        <TabPanel value={tabValue} index={1}>
          {connectedIntegrations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <IntegrationInstructions sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No integrations connected</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect your existing systems to sync inventory and data automatically
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setTabValue(0)}>
                Browse Integrations
              </Button>
            </Box>
          ) : (
            <List>
              {connectedIntegrations.map((integration, index) => (
                <React.Fragment key={integration.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'grey.100', fontSize: 24 }}>
                        {integration.logo}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {integration.name}
                          <Chip 
                            size="small" 
                            label={syncing === integration.id ? 'Syncing...' : 'Connected'} 
                            color={syncing === integration.id ? 'info' : 'success'} 
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" component="span">
                            Last sync: {integration.last_sync ? formatUKDateTime(integration.last_sync) : 'Never'}
                          </Typography>
                          <Typography variant="body2" component="span" sx={{ mx: 2 }}>|</Typography>
                          <Typography variant="body2" component="span">
                            Frequency: {integration.syncFrequency}
                          </Typography>
                          <Typography variant="body2" component="span" sx={{ mx: 2 }}>|</Typography>
                          <Typography variant="body2" component="span">
                            Records: {formatUKNumber(integration.recordsSynced)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Sync Now">
                        <IconButton 
                          onClick={() => handleSyncNow(integration.id)}
                          disabled={syncing === integration.id}
                        >
                          {syncing === integration.id ? <CircularProgress size={20} /> : <Refresh />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Settings">
                        <IconButton>
                          <Settings />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Disconnect">
                        <IconButton color="error" onClick={() => handleDisconnect(integration.id)}>
                          <LinkOff />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Sync History */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <History sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">Sync history coming soon</Typography>
            <Typography variant="body2" color="text.secondary">
              View detailed logs of all data synchronizations
            </Typography>
          </Box>
        </TabPanel>
      </Card>

      {/* Connect Dialog */}
      <Dialog open={connectDialogOpen} onClose={() => setConnectDialogOpen(false)} maxWidth="sm" fullWidth>
        {selectedIntegration && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'grey.100', fontSize: 24 }}>
                  {selectedIntegration.logo}
                </Avatar>
                Connect {selectedIntegration.name}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stepper activeStep={connectStep} sx={{ my: 3 }}>
                {connectSteps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {connectStep === 0 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <AlertTitle>Authentication Required</AlertTitle>
                    Enter your API credentials for {selectedIntegration.name}.
                  </Alert>
                  <TextField
                    fullWidth
                    label="API Key"
                    placeholder="Enter your API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="API Secret"
                    type="password"
                    placeholder="Enter your API secret"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                  />
                </Box>
              )}

              {connectStep === 1 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>Configure what to sync:</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><Inventory /></ListItemIcon>
                      <ListItemText primary="Products & Inventory" secondary="Sync product catalog and stock levels" />
                      <Switch checked={syncProducts} onChange={(e) => setSyncProducts(e.target.checked)} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Assessment /></ListItemIcon>
                      <ListItemText primary="Inventory Levels" secondary="Real-time stock updates" />
                      <Switch checked={syncInventory} onChange={(e) => setSyncInventory(e.target.checked)} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LocalShipping /></ListItemIcon>
                      <ListItemText primary="Orders" secondary="Sync purchase order history" />
                      <Switch checked={syncOrders} onChange={(e) => setSyncOrders(e.target.checked)} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Business /></ListItemIcon>
                      <ListItemText primary="Suppliers" secondary="Import supplier/vendor data" />
                      <Switch checked={syncSuppliers} onChange={(e) => setSyncSuppliers(e.target.checked)} />
                    </ListItem>
                  </List>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Sync Frequency</InputLabel>
                    <Select 
                      value={syncFrequency} 
                      label="Sync Frequency"
                      onChange={(e) => setSyncFrequency(e.target.value as 'hourly' | 'daily' | 'weekly')}
                    >
                      <MenuItem value="hourly">Every hour</MenuItem>
                      <MenuItem value="daily">Every day</MenuItem>
                      <MenuItem value="weekly">Every week</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {connectStep === 2 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>Ready to Connect!</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "Connect" to establish the connection to {selectedIntegration.name}. 
                    Your first sync will begin automatically.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
              <Box sx={{ flexGrow: 1 }} />
              {connectStep > 0 && connectStep < 2 && (
                <Button onClick={() => setConnectStep(connectStep - 1)}>Back</Button>
              )}
              {connectStep < 2 ? (
                <Button variant="contained" onClick={() => setConnectStep(connectStep + 1)}>
                  {connectStep === 0 ? 'Next' : 'Next'}
                </Button>
              ) : (
                <Button variant="contained" color="success" onClick={completeConnection}>
                  Connect
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrationsPage;
